import { Task } from 'taskarian';
import { Decoder, field, string, succeed, array } from 'jsonous';
import { Maybe } from 'maybeasy';
import { Link } from '../Resource/Types';
import { callApi, AppyError } from '../Appy';
import { optionalField } from '../Decoders';
import { RationaleItem } from '../DashboardSummaryStore/Types';

export interface QuickSightSummaryRequest {
  dashboardId: string;
  locale?: string;
  source?: 'dashboard' | 'reports';
  filters?: {
    date_type?: string;
    start_date?: string;
    end_date?: string;
  };
}

export interface QuickSightSummaryMetadata {
  dashboardId: string;
  tabId: Maybe<string>;
  generatedAt: string;
}

export interface QuickSightSummaryResponse {
  summary: string;
  headline: Maybe<string>;
  rationale: RationaleItem[];
  fullAnalysis: Maybe<string>;
  metadata: Maybe<QuickSightSummaryMetadata>;
  status: string;
  error: Maybe<string>;
  message: Maybe<string>;
  source: Maybe<string>;
}

// Decoder for metadata
const metadataDecoder: Decoder<QuickSightSummaryMetadata> = succeed({})
  .assign('dashboardId', field('dashboard_id', string))
  .assign('tabId', optionalField('tab_id', string))
  .assign('generatedAt', field('generated_at', string));

// Decoder for rationale item
const rationaleItemDecoder: Decoder<RationaleItem> = succeed({})
  .assign('quote', field('quote', string))
  .assign('source', field('source', string));

// Decoder for rationale array with fallback to empty array
const rationaleArrayDecoder: Decoder<RationaleItem[]> = array(rationaleItemDecoder);

// Decoder for QuickSight summary response
const quickSightSummaryResponseDecoder: Decoder<QuickSightSummaryResponse> = succeed({})
  .assign('summary', field('summary', string))
  .assign('headline', optionalField('headline', string))
  .assign(
    'rationale',
    optionalField('rationale', rationaleArrayDecoder).map((maybeArr) =>
      maybeArr.getOrElse(() => []),
    ),
  )
  .assign('fullAnalysis', optionalField('full_analysis', string))
  .assign('metadata', optionalField('metadata', metadataDecoder))
  .assign('status', field('status', string))
  .assign('error', optionalField('error', string))
  .assign('message', optionalField('message', string))
  .assign('source', optionalField('source', string));

/**
 * Call the backend API to generate an AI summary for QuickSight dashboard data
 */
export const generateQuickSightSummary = (
  link: Link,
  request: QuickSightSummaryRequest,
): Task<AppyError, QuickSightSummaryResponse> => {
  const payload = {
    dashboard_id: request.dashboardId,
    locale: request.locale || 'en',
    source: request.source || 'dashboard',
    filters: request.filters || {},
  };

  return callApi(quickSightSummaryResponseDecoder, payload)(link);
};
